/**
 * Client-side port of Companion-Apps/iOS/tools/merge_app_log.py.
 *
 * AppLogger uploads concatenate whole blocks back-to-back (app log lines, then
 * an "=== OS / Framework Logs ===" section). Each block is internally
 * chronological but the blocks overlap in time, so reading top to bottom does
 * not read events in the order they happened. This re-parses every
 * timestamped entry regardless of which block it came from and re-emits them
 * as one interleaved chronological stream.
 *
 * A log entry can span multiple physical lines (e.g. a printed dictionary) —
 * only the first line carries the "yyyy-MM-dd HH:mm:ss.SSS" prefix.
 */

export const RAW_APP_LOG_WARNING =
  'This log contains the app side logs concatenated (at the end) with system level logs. Please remember this when parsing.';

const TIMESTAMP_RE = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}) (.*)$/;
const SECTION_MARKER_RE = /^=+.*=+$/;

const parseTimestamp = (tsStr) => {
  const [datePart, timePart] = tsStr.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, secPart] = timePart.split(':');
  const [second, millis] = secPart.split('.').map(Number);
  return new Date(year, month - 1, day, Number(hour), Number(minute), second, millis).getTime();
};

const parseEntries = (lines) => {
  const entries = [];
  let currentTs = null;
  let currentLines = null;

  const flush = () => {
    if (currentTs !== null) {
      entries.push({ ts: currentTs, text: currentLines.join('\n') });
    }
  };

  for (const line of lines) {
    const match = TIMESTAMP_RE.exec(line);
    if (match) {
      flush();
      currentTs = parseTimestamp(match[1]);
      currentLines = [line];
      continue;
    }

    const stripped = line.trim();
    if (!stripped || SECTION_MARKER_RE.test(stripped)) {
      continue; // separator between blocks, carries no ordering information
    }

    if (currentLines === null) {
      continue; // content before any timestamped line — can't be ordered, drop it
    }

    currentLines.push(line);
  }

  flush();
  return entries;
};

/**
 * Rebuild an AppLogger bundle's raw text into strict chronological order.
 * Returns null if the content has no timestamped entries.
 */
export const reorderAppLogContent = (content) => {
  const lines = content.split(/\r?\n/);
  const entries = parseEntries(lines);
  if (entries.length === 0) {
    return null;
  }

  entries.sort((a, b) => a.ts - b.ts); // Array.sort is stable (ES2019+): ties keep original order

  return entries.map((e) => e.text).join('\n') + '\n';
};
